/**
 * versona_charts — PURE time-series layer for the Versona Universe "Charts" tab.
 *
 * Every series is computed from append-only history the server already keeps
 * (chronicle kinds, graves, crime cases, war clashes, market rounds, npc events)
 * bucketed over a chosen window; LEVEL series (population) are reconstructed
 * BACKWARDS from the current count through births/arrivals/deaths/exiles, so the
 * window shows real growth or decline without having had to sample it.
 *
 * `drawChart` renders any number of series on one canvas — "scaled" puts every
 * series on its own 0..100 % axis so growth and decline of different magnitudes
 * compare side by side; "raw" shares one axis. No chart library: the client is
 * self-contained and the tab must work offline of any CDN.
 */

export type ChartGroup = 'population' | 'conflict' | 'economy' | 'society';
export type SeriesKind = 'rate' | 'level';

/** One timestamped fact from a history table, flattened to what the series need. */
export interface ChartEvent {
  at: number;                 // epoch ms
  source: 'chronicle' | 'grave' | 'crime' | 'clash' | 'market' | 'npcEvent' | 'war' | 'migration';
  kind: string;               // chronicle/npc_event kind · grave cause · crime kind · market commodity · war status
  settlementId: string;
  value?: number;             // market volume · clash casualties …
  price?: number;             // market avg price
  unfilled?: number;          // market unfilled demand
}

export interface SeriesDef {
  id: string;
  label: string;
  group: ChartGroup;
  color: string;
  kind: SeriesKind;
  unit: string;
  /** Per-event contribution (0 = not this series). Rate series sum contributions per bin. */
  weigh?: (e: ChartEvent, ctx: SeriesContext) => number;
  /** Per-bin aggregate for averaged series (e.g. prices): returns the bin value from its events. */
  aggregate?: (events: ChartEvent[], ctx: SeriesContext) => number | null;
  /** Level series are derived from the whole event list + the current count. */
  level?: (events: ChartEvent[], bins: number[], ctx: SeriesContext) => number[];
}

export interface SeriesContext {
  now: number;
  settlementId: string | null;  // null = the whole Commons
  commodity: string | null;     // null = all commodities
  currentPopulation: number;
}

export interface ComputedSeries {
  id: string;
  label: string;
  color: string;
  unit: string;
  kind: SeriesKind;
  values: Array<number | null>;
  min: number;
  max: number;
  first: number | null;
  last: number | null;
  deltaPct: number | null;
}

export interface WindowDef { label: string; ms: number | null }
export const WINDOWS: readonly WindowDef[] = [
  { label: '1h', ms: 3_600_000 }, { label: '6h', ms: 6 * 3_600_000 }, { label: '24h', ms: 24 * 3_600_000 },
  { label: '3d', ms: 3 * 86_400_000 }, { label: '7d', ms: 7 * 86_400_000 }, { label: '30d', ms: 30 * 86_400_000 },
  { label: 'all', ms: null },
];

const ck = (kind: string) => (e: ChartEvent): number => (e.source === 'chronicle' && e.kind === kind ? 1 : 0);
const nk = (kind: string) => (e: ChartEvent): number => (e.source === 'npcEvent' && e.kind === kind ? 1 : 0);
const inScope = (e: ChartEvent, ctx: SeriesContext): boolean => ctx.settlementId === null || e.settlementId === ctx.settlementId;
const marketOf = (e: ChartEvent, ctx: SeriesContext): boolean => e.source === 'market' && (ctx.commodity === null || e.kind === ctx.commodity);

/**
 * Population at the END of each bin, reconstructed backwards from the current
 * count: pop(t) = now − (births + arrivals after t) + (deaths + exiles after t).
 */
export function reconstructPopulation(events: ChartEvent[], binEnds: number[], ctx: SeriesContext): number[] {
  const plus = (e: ChartEvent): number => (e.source === 'chronicle' && (e.kind === 'birth' || e.kind === 'arrival') ? 1 : 0);
  const minus = (e: ChartEvent): number => (e.source === 'chronicle' && (e.kind === 'death' || e.kind === 'exile') ? 1 : 0);
  const scoped = events.filter((e) => inScope(e, ctx) && e.source === 'chronicle');
  return binEnds.map((t) => {
    let net = 0;
    for (const e of scoped) if (e.at > t) net += plus(e) - minus(e);
    return ctx.currentPopulation - net;
  });
}

export const SERIES: readonly SeriesDef[] = [
  // ── population ──
  { id: 'population', label: 'Population', group: 'population', color: '#7fd1ff', kind: 'level', unit: 'versonas', level: reconstructPopulation },
  { id: 'births', label: 'Births', group: 'population', color: '#8cff9c', kind: 'rate', unit: '/bin', weigh: ck('birth') },
  { id: 'deaths', label: 'Deaths', group: 'population', color: '#ff8c8c', kind: 'rate', unit: '/bin', weigh: ck('death') },
  { id: 'deaths_slain', label: 'Deaths · slain', group: 'population', color: '#ff5d5d', kind: 'rate', unit: '/bin', weigh: (e) => (e.source === 'grave' && e.kind === 'slain' ? 1 : 0) },
  { id: 'deaths_age', label: 'Deaths · old age', group: 'population', color: '#d9a3a3', kind: 'rate', unit: '/bin', weigh: (e) => (e.source === 'grave' && e.kind === 'old_age' ? 1 : 0) },
  { id: 'deaths_war', label: 'Deaths · war', group: 'population', color: '#ff7a3d', kind: 'rate', unit: '/bin', weigh: (e) => (e.source === 'grave' && e.kind === 'war' ? 1 : 0) },
  { id: 'deaths_executed', label: 'Deaths · executed', group: 'population', color: '#c46bff', kind: 'rate', unit: '/bin', weigh: (e) => (e.source === 'grave' && e.kind === 'executed' ? 1 : 0) },
  { id: 'unions', label: 'Unions', group: 'population', color: '#ffb7f5', kind: 'rate', unit: '/bin', weigh: ck('union') },
  { id: 'arrivals', label: 'Arrivals', group: 'population', color: '#b3f0ff', kind: 'rate', unit: '/bin', weigh: ck('arrival') },
  { id: 'exiles', label: 'Exiles', group: 'population', color: '#c9a4ff', kind: 'rate', unit: '/bin', weigh: ck('exile') },
  { id: 'migrations', label: 'Migrations (arrived)', group: 'population', color: '#9ad4c8', kind: 'rate', unit: '/bin', weigh: (e) => (e.source === 'migration' ? 1 : 0) },
  { id: 'foundings', label: 'Colony foundings', group: 'population', color: '#ffe08c', kind: 'rate', unit: '/bin', weigh: ck('founding') },
  // ── conflict ──
  { id: 'wars', label: 'Wars declared', group: 'conflict', color: '#ff4d4d', kind: 'rate', unit: '/bin', weigh: ck('war') },
  { id: 'battles', label: 'Battles', group: 'conflict', color: '#ff944d', kind: 'rate', unit: '/bin', weigh: ck('battle') },
  { id: 'clashes', label: 'War clashes', group: 'conflict', color: '#ffb84d', kind: 'rate', unit: '/bin', weigh: (e) => (e.source === 'clash' ? 1 : 0) },
  { id: 'peace', label: 'Peace made', group: 'conflict', color: '#9cffd9', kind: 'rate', unit: '/bin', weigh: ck('peace') },
  { id: 'crimes', label: 'Crimes opened', group: 'conflict', color: '#e07cff', kind: 'rate', unit: '/bin', weigh: (e) => (e.source === 'crime' ? 1 : 0) },
  { id: 'homicides', label: 'Homicides', group: 'conflict', color: '#ff6cc0', kind: 'rate', unit: '/bin', weigh: (e) => (e.source === 'crime' && e.kind === 'homicide' ? 1 : 0) },
  { id: 'thefts', label: 'Thefts', group: 'conflict', color: '#d49cff', kind: 'rate', unit: '/bin', weigh: (e) => (e.source === 'crime' && e.kind === 'theft' ? 1 : 0) },
  { id: 'verdicts', label: 'Verdicts', group: 'conflict', color: '#bfa6ff', kind: 'rate', unit: '/bin', weigh: ck('verdict') },
  { id: 'brawls', label: 'Brawls', group: 'conflict', color: '#ffa07a', kind: 'rate', unit: '/bin', weigh: nk('brawl') },
  { id: 'feuds', label: 'Feuds', group: 'conflict', color: '#ff7f50', kind: 'rate', unit: '/bin', weigh: nk('feud') },
  // ── economy ──
  { id: 'market_volume', label: 'Market volume', group: 'economy', color: '#ffd166', kind: 'rate', unit: 'units/bin', weigh: (e, ctx) => (marketOf(e, ctx) ? e.value ?? 0 : 0) },
  { id: 'market_price', label: 'Avg price', group: 'economy', color: '#f4a261', kind: 'rate', unit: 'ⓒ', aggregate: (evs, ctx) => { let v = 0, pv = 0; for (const e of evs) if (marketOf(e, ctx) && e.price !== undefined) { const w = Math.max(1, e.value ?? 1); v += w; pv += w * e.price; } return v > 0 ? pv / v : null; } },
  { id: 'market_unfilled', label: 'Unfilled demand', group: 'economy', color: '#e76f51', kind: 'rate', unit: 'units/bin', weigh: (e, ctx) => (marketOf(e, ctx) ? e.unfilled ?? 0 : 0) },
  { id: 'market_rounds', label: 'Market rounds', group: 'economy', color: '#ffe8a3', kind: 'rate', unit: '/bin', weigh: (e, ctx) => (marketOf(e, ctx) ? 1 : 0) },
  { id: 'shortages', label: 'Shortages', group: 'economy', color: '#ff9e9e', kind: 'rate', unit: '/bin', weigh: nk('shortage') },
  { id: 'caravans', label: 'Caravans', group: 'economy', color: '#c7f0a8', kind: 'rate', unit: '/bin', weigh: (e) => ((e.source === 'chronicle' || e.source === 'npcEvent') && e.kind === 'caravan' ? 1 : 0) },
  { id: 'retrained', label: 'Retrained', group: 'economy', color: '#a8d8f0', kind: 'rate', unit: '/bin', weigh: ck('retrained') },
  { id: 'sales', label: 'Schematic sales', group: 'economy', color: '#ffc6a5', kind: 'rate', unit: '/bin', weigh: nk('schematic_sale') },
  // ── society ──
  { id: 'festivals', label: 'Festivals', group: 'society', color: '#ffd6e8', kind: 'rate', unit: '/bin', weigh: ck('festival') },
  { id: 'feasts', label: 'Feasts', group: 'society', color: '#f0c8ff', kind: 'rate', unit: '/bin', weigh: nk('feast') },
  { id: 'rites', label: 'Rites', group: 'society', color: '#d0d0ff', kind: 'rate', unit: '/bin', weigh: nk('rite') },
  { id: 'faith', label: 'Faith events', group: 'society', color: '#e6e6ff', kind: 'rate', unit: '/bin', weigh: ck('faith') },
  { id: 'offices', label: 'Offices taken', group: 'society', color: '#bde0fe', kind: 'rate', unit: '/bin', weigh: ck('office') },
  { id: 'elections', label: 'Elections', group: 'society', color: '#a2d2ff', kind: 'rate', unit: '/bin', weigh: (e) => ((e.source === 'chronicle' || e.source === 'npcEvent') && e.kind === 'election' ? 1 : 0) },
  { id: 'speeches', label: 'Speeches', group: 'society', color: '#cdb4db', kind: 'rate', unit: '/bin', weigh: nk('speech') },
  { id: 'scenes', label: 'Street scenes', group: 'society', color: '#ffc8dd', kind: 'rate', unit: '/bin', weigh: nk('scene') },
];

export const DEFAULT_SERIES = ['population', 'births', 'deaths'];

export function seriesById(id: string): SeriesDef | undefined { return SERIES.find((s) => s.id === id); }

/** Bin edges for [t0, t1] — `bins` equal slices; returns the END time of each bin. */
export function binEnds(t0: number, t1: number, bins: number): number[] {
  const n = Math.max(1, Math.floor(bins));
  const w = (t1 - t0) / n;
  return Array.from({ length: n }, (_, i) => t0 + w * (i + 1));
}

/** Index of the bin an event falls into, or -1 when outside [t0, t1]. */
export function binIndex(at: number, t0: number, t1: number, bins: number): number {
  if (at < t0 || at > t1) return -1;
  const n = Math.max(1, Math.floor(bins));
  const i = Math.floor(((at - t0) / (t1 - t0)) * n);
  return Math.min(n - 1, Math.max(0, i));
}

/** Sum a weigh() over events per bin. */
export function bucketize(events: ChartEvent[], t0: number, t1: number, bins: number, weigh: (e: ChartEvent, ctx: SeriesContext) => number, ctx: SeriesContext): number[] {
  const out = new Array<number>(Math.max(1, Math.floor(bins))).fill(0);
  for (const e of events) {
    if (!inScope(e, ctx)) continue;
    const i = binIndex(e.at, t0, t1, bins);
    if (i < 0) continue;
    out[i] += weigh(e, ctx);
  }
  return out;
}

export interface ComputeOptions { t0: number; t1: number; bins: number; ctx: SeriesContext }

export function computeSeries(def: SeriesDef, events: ChartEvent[], o: ComputeOptions): ComputedSeries {
  const ends = binEnds(o.t0, o.t1, o.bins);
  let values: Array<number | null>;
  if (def.level) values = def.level(events, ends, o.ctx);
  else if (def.aggregate) {
    const perBin: ChartEvent[][] = ends.map(() => []);
    for (const e of events) { if (!inScope(e, o.ctx)) continue; const i = binIndex(e.at, o.t0, o.t1, o.bins); if (i >= 0) perBin[i].push(e); }
    values = perBin.map((evs) => def.aggregate!(evs, o.ctx));
  } else values = bucketize(events, o.t0, o.t1, o.bins, def.weigh ?? (() => 0), o.ctx);
  const nums = values.filter((v): v is number => v !== null && Number.isFinite(v));
  const first = nums.length ? nums[0] : null;
  const last = nums.length ? nums[nums.length - 1] : null;
  const deltaPct = first !== null && last !== null && first !== 0 ? ((last - first) / Math.abs(first)) * 100 : first === 0 && last !== null && last !== 0 ? 100 : first !== null && last !== null ? 0 : null;
  return { id: def.id, label: def.label, color: def.color, unit: def.unit, kind: def.kind, values, min: nums.length ? Math.min(...nums) : 0, max: nums.length ? Math.max(...nums) : 0, first, last, deltaPct };
}

/** 0..1 per series (its own min..max) so unlike magnitudes compare on one chart. */
export function normalize(values: Array<number | null>, min: number, max: number): Array<number | null> {
  const span = max - min;
  return values.map((v) => (v === null ? null : span > 0 ? (v - min) / span : 0.5));
}

/** Window → [t0, t1]; 'all' spans from the earliest event. */
export function windowRange(win: WindowDef, now: number, events: ChartEvent[]): { t0: number; t1: number } {
  if (win.ms !== null) return { t0: now - win.ms, t1: now };
  let t0 = now;
  for (const e of events) if (e.at < t0) t0 = e.at;
  return { t0: Math.min(t0, now - 60_000), t1: now };
}

/** A sensible bin count for a window: ~48 bins, fewer for very short windows. */
export function defaultBins(win: WindowDef): number { return win.ms !== null && win.ms <= 3_600_000 ? 30 : 48; }

export function formatValue(v: number | null, unit: string): string {
  if (v === null) return '—';
  const s = Math.abs(v) >= 100 ? v.toFixed(0) : Math.abs(v) >= 10 ? v.toFixed(1) : v.toFixed(2).replace(/\.?0+$/, '');
  return unit === 'ⓒ' ? `${s}ⓒ` : unit === '/bin' || unit === '' ? s : `${s} ${unit}`;
}

export function formatTime(t: number, spanMs: number): string {
  const d = new Date(t);
  const hh = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  if (spanMs <= 36 * 3_600_000) return hh;
  return `${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${hh}`;
}

export interface DrawOptions { t0: number; t1: number; mode: 'scaled' | 'raw'; hoverIndex?: number | null; background?: string }
export interface ChartLayout { left: number; right: number; top: number; bottom: number; bins: number }

/** Render the series on a 2D canvas. Returns the plot layout for hover hit-testing. */
export function drawChart(canvas: HTMLCanvasElement, series: ComputedSeries[], o: DrawOptions): ChartLayout {
  const ctx = canvas.getContext('2d');
  const W = canvas.width, H = canvas.height;
  const L = 54, R = 14, T = 12, B = 28;
  const layout: ChartLayout = { left: L, right: W - R, top: T, bottom: H - B, bins: series[0]?.values.length ?? 0 };
  if (!ctx) return layout;
  ctx.clearRect(0, 0, W, H);
  ctx.fillStyle = o.background ?? '#0b1119';
  ctx.fillRect(0, 0, W, H);
  const pw = layout.right - layout.left, ph = layout.bottom - layout.top;
  // grid
  ctx.strokeStyle = '#1e2a3a'; ctx.lineWidth = 1;
  for (let i = 0; i <= 4; i++) { const y = layout.top + (ph * i) / 4; ctx.beginPath(); ctx.moveTo(layout.left, y); ctx.lineTo(layout.right, y); ctx.stroke(); }
  // shared axis for raw mode
  let rawMin = Infinity, rawMax = -Infinity;
  for (const s of series) { if (s.min < rawMin) rawMin = s.min; if (s.max > rawMax) rawMax = s.max; }
  if (!Number.isFinite(rawMin)) { rawMin = 0; rawMax = 1; }
  if (rawMax === rawMin) { rawMax = rawMin + 1; }
  ctx.fillStyle = '#7d93ad'; ctx.font = '10px system-ui'; ctx.textAlign = 'right';
  for (let i = 0; i <= 4; i++) {
    const y = layout.top + (ph * i) / 4;
    const label = o.mode === 'scaled' ? `${100 - i * 25}%` : formatValue(rawMax - ((rawMax - rawMin) * i) / 4, '');
    ctx.fillText(label, layout.left - 6, y + 3);
  }
  // time axis
  ctx.textAlign = 'center';
  const span = o.t1 - o.t0;
  for (let i = 0; i <= 4; i++) { const x = layout.left + (pw * i) / 4; ctx.fillText(formatTime(o.t0 + (span * i) / 4, span), x, H - 10); }
  // lines
  for (const s of series) {
    const n = s.values.length;
    if (!n) continue;
    const vals = o.mode === 'scaled' ? normalize(s.values, s.min, s.max) : s.values.map((v) => (v === null ? null : (v - rawMin) / (rawMax - rawMin)));
    ctx.strokeStyle = s.color; ctx.lineWidth = s.kind === 'level' ? 2.2 : 1.6; ctx.beginPath();
    let pen = false;
    vals.forEach((v, i) => {
      if (v === null) { pen = false; return; }
      const x = layout.left + (pw * (i + 0.5)) / n;
      const y = layout.bottom - ph * v;
      if (!pen) { ctx.moveTo(x, y); pen = true; } else ctx.lineTo(x, y);
    });
    ctx.stroke();
  }
  // hover line
  if (o.hoverIndex !== undefined && o.hoverIndex !== null && layout.bins > 0) {
    const x = layout.left + (pw * (o.hoverIndex + 0.5)) / layout.bins;
    ctx.strokeStyle = '#ffffff55'; ctx.lineWidth = 1; ctx.beginPath(); ctx.moveTo(x, layout.top); ctx.lineTo(x, layout.bottom); ctx.stroke();
  }
  return layout;
}
